import React, { useState, useEffect } from 'react';
import { Container, Box, Grid, Typography, CircularProgress, Alert, Paper, FormControl, InputLabel, Select, MenuItem, Button, Chip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import Layout from '../components/Layout';
import SectionHeader from '../components/SectionHeader';
import ServiceCard from '../components/ServiceCard';
import { SERVICE_CATEGORIES } from '../config/categories';

const AllServices = () => {
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get category from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromUrl = params.get('category') || '';
    setSelectedCategory(categoryFromUrl);
  }, [location.search]);

  // Fetch all services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('📡 Fetching all services');

        const response = await apiService.getServices('');

        console.log('✅ Services response:', response);

        if (response.success) {
          // Transform expert data to match ServiceCard expectations
          const transformedServices = (response.data || []).map(s => ({
            id: s.id,
            name: s.name,
            photo: s.photo,
            field: s.field,
            category: s.category,
            address: s.address,
            phone: s.phone1,
            phone1: s.phone1,
            phone2: s.phone2,
            rate: s.rate,
            note: s.note,
            rating: s.rating || 4.5,
            city: s.city || 'Bisauli'
          }));

          setServices(transformedServices);
          setError(null);
          console.log('✅ Transformed services:', transformedServices);
        } else {
          setServices([]);
          setError(response.message || 'Failed to load services');
        }
      } catch (error) {
        console.error('❌ Failed to fetch services:', error);
        setError('Error loading services. Please try again later.');
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = services;

    // Filter by category
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(s => s.category === selectedCategory || s.field === selectedCategory);
    }

    setFilteredServices(filtered);
  }, [services, selectedCategory]);

  const handleClearFilters = () => {
    setSelectedCategory('');
    // Update URL in same window
    window.history.replaceState({}, '', '/all-services');
  };

  const handleCategoryChange = (newCategory) => {
    setSelectedCategory(newCategory);
    // Update URL in same window
    if (newCategory && newCategory !== 'all') {
      window.history.replaceState({}, '', `/all-services?category=${newCategory}`);
    } else {
      window.history.replaceState({}, '', '/all-services');
    }
  };

  // Get category label for header
  const getCategoryLabel = () => {
    if (!selectedCategory || selectedCategory === 'all') {
      return 'All Services';
    }
    const category = SERVICE_CATEGORIES.find(c => c.value === selectedCategory);
    return category ? `${category.icon} ${category.labelEn}` : 'All Services';
  };

  return (
    <Layout>
      <Box sx={{ bgcolor: '#f9fafb', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <SectionHeader
            title={getCategoryLabel()}
            subtitle={selectedCategory ? `Explore professionals in ${SERVICE_CATEGORIES.find(c => c.value === selectedCategory)?.labelEn?.replace(/^[^\s]*\s/, '').trim()}` : "Browse our trusted professionals and service providers"}
            icon="👤"
          />
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        {/* Filters Section */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4, bgcolor: '#f7fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#2d3748' }}>
            🔍 Filters
          </Typography>

          <Grid container spacing={3}>
            {/* Category Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => handleCategoryChange(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {SERVICE_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.labelEn?.replace(/^[^\s]*\s/, '').trim()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Clear Filters Button */}
          {selectedCategory && (
            <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={`Category: ${SERVICE_CATEGORIES.find(c => c.value === selectedCategory)?.labelEn?.replace(/^[^\s]*\s/, '').trim()}`}
                onDelete={() => handleClearFilters()}
                color="primary"
                variant="outlined"
              />
              <Button 
                size="small" 
                variant="outlined" 
                onClick={handleClearFilters}
                sx={{ ml: 'auto' }}
              >
                Clear Filter
              </Button>
            </Box>
          )}
        </Paper>

        {/* Results Count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ color: '#718096', fontWeight: 500 }}>
            Showing <strong>{filteredServices.length}</strong> of <strong>{services.length}</strong> services
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Services Grid */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filteredServices.length > 0 ? (
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
            {filteredServices.map((s) => (
              <Grid item key={s.id} xs={12} sm={6} md={3}>
                <ServiceCard service={s} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              👨‍💼 No services found matching your filters.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your filters to see more results.
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleClearFilters}
              sx={{ mt: 2, background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)' }}
            >
              Clear Filter
            </Button>
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default AllServices;